import { Field } from "@/components/admin/field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Search, SearchCreate, SearchStatus, Tier } from "@ow/shared";
import { useState } from "react";

const TIERS: { value: Tier; label: string }[] = [
  { value: "csuite", label: "C-Suite" },
  { value: "vp_director", label: "VP / Director" },
  { value: "general", label: "General" },
];
const STATUSES: SearchStatus[] = ["active", "on_hold", "closed"];

const numOrNull = (s: string) => (s.trim() === "" ? null : Number.parseFloat(s.replace(/[,$]/g, "")));

export function SearchForm({
  initial,
  onSubmit,
  submitting,
  onCancel,
}: {
  initial?: Search;
  onSubmit: (body: SearchCreate) => void;
  submitting?: boolean;
  onCancel?: () => void;
}) {
  const [tier, setTier] = useState<Tier>(initial?.tier ?? "csuite");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [fn, setFn] = useState(initial?.function ?? "");
  const [owner, setOwner] = useState(initial?.ownerName ?? "");
  const [status, setStatus] = useState<SearchStatus>(initial?.status ?? "active");
  const [budgetMin, setBudgetMin] = useState(initial?.budgetMin?.toString() ?? "");
  const [budgetMax, setBudgetMax] = useState(initial?.budgetMax?.toString() ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      tier,
      title: title.trim(),
      function: fn || null,
      ownerName: owner || null,
      status,
      budgetMin: numOrNull(budgetMin),
      budgetMax: numOrNull(budgetMax),
      budgetCurrency: initial?.budgetCurrency ?? "USD",
      notes: notes || null,
    });
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3 rounded-xl border bg-card p-4">
      <Field label="Tier">
        <Select value={tier} onValueChange={(v) => setTier(v as Tier)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIERS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Status">
        <Select value={status} onValueChange={(v) => setStatus(v as SearchStatus)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Role title" className="col-span-2">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Chief Financial Officer" />
      </Field>
      <Field label="Function">
        <Input value={fn} onChange={(e) => setFn(e.target.value)} placeholder="Finance / Technology / Ops" />
      </Field>
      <Field label="Internal owner">
        <Input value={owner} onChange={(e) => setOwner(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Budget min">
          <Input value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Budget max">
          <Input value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} inputMode="decimal" />
        </Field>
      </div>
      <Field label="Notes" className="col-span-2">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <div className="col-span-2 flex gap-2">
        <Button type="submit" disabled={submitting || !title.trim()}>
          {initial ? "Save search" : "Create search"}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}
